import { Session } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";

import { retryWithBackoff } from "./retryWithBackoff";
import { trackEdgeFunctionCall } from "./sentryBreadcrumbs";

/**
 * Edge Function Helper Library
 * 
 * Pure functions (not hooks) for invoking Supabase edge functions with consistent
 * security patterns. Can be used anywhere - including context providers, async 
 * callbacks, and service workers where React hooks are not available.
 * 
 * Security features:
 * - Automatic token refresh
 * - Retry logic with exponential backoff
 * - Consistent error handling
 * - Type-safe interfaces
 */

export interface EdgeFunctionOptions {
  body?: Record<string, any>;
  headers?: Record<string, string>;
  maxRetries?: number;
  retryDelay?: number;
  logErrors?: boolean;
}

export interface EdgeFunctionResult<T> {
  data: T | null;
  error: Error | null;
}

const DEFAULT_OPTIONS: Partial<EdgeFunctionOptions> = {
  maxRetries: 2,
  retryDelay: 1000,
  logErrors: true,
};

/**
 * Check if a session token is expired or about to expire (within 60 seconds)
 */
function isTokenExpired(session: Session | null): boolean {
  if (!session?.expires_at) return true;
  
  const expiresAt = session.expires_at * 1000; // Convert to milliseconds
  const now = Date.now();
  const bufferTime = 60 * 1000; // 60 second buffer
  
  return expiresAt - now < bufferTime;
}

/**
 * Get a valid session, refreshing the token if needed
 */
async function getValidSession(): Promise<{ session: Session | null; error: Error | null }> {
  try {
    // eslint-disable-next-line no-restricted-properties
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { session: null, error: new Error(`Session retrieval failed: ${sessionError.message}`) };
    }
    
    if (!session) {
      return { session: null, error: new Error("No active session found") };
    }
    
    // Check if token needs refresh
    if (isTokenExpired(session)) {
      console.log("[EdgeFunctionHelper] Token expired, refreshing...");
      // eslint-disable-next-line no-restricted-properties
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !refreshedSession) {
        return { session: null, error: new Error(`Token refresh failed: ${refreshError?.message || "Unknown error"}`) };
      }
      
      return { session: refreshedSession, error: null };
    }
    
    return { session, error: null };
  } catch (err) {
    return { session: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}

/**
 * Invoke an authenticated edge function with automatic session management
 * 
 * This helper:
 * - Gets current session
 * - Checks token expiry and refreshes if needed
 * - Adds Authorization header
 * - Retries on network failures
 * - Returns consistent result format
 * 
 * @example
 * const { data, error } = await invokeAuthenticatedEdgeFunction("chat", {
 *   body: { message: "Hello" },
 *   maxRetries: 3
 * });
 */
export async function invokeAuthenticatedEdgeFunction<T = any>(
  functionName: string,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const startTime = Date.now();
  
  try {
    // Get valid session with token refresh if needed
    const { session, error: sessionError } = await getValidSession();
    
    if (sessionError || !session) {
      const error = sessionError || new Error("Authentication required");
      if (opts.logErrors) {
        console.error(`[EdgeFunctionHelper] Auth failed for ${functionName}:`, error);
      }
      trackEdgeFunctionCall(functionName, false, Date.now() - startTime, error.message);
      return { data: null, error };
    }
    
    // Invoke with retry logic
    const result = await retryWithBackoff(
      async () => {
        // eslint-disable-next-line no-restricted-syntax
        const { data, error } = await supabase.functions.invoke<T>(functionName, {
          body: opts.body,
          headers: {
            ...opts.headers,
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        
        if (error) throw error;
        return data;
      },
      {
        maxRetries: opts.maxRetries,
        initialDelayMs: opts.retryDelay,
      }
    );
    
    trackEdgeFunctionCall(functionName, true, Date.now() - startTime);
    return { data: result, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    if (opts.logErrors) {
      console.error(`[EdgeFunctionHelper] Error invoking ${functionName}:`, error);
    }
    trackEdgeFunctionCall(functionName, false, Date.now() - startTime, error.message);
    return { data: null, error };
  }
}

/**
 * Invoke an edge function using an existing session
 * 
 * Useful immediately after sign-in/sign-up when you already have a fresh session.
 * Still validates token expiry and can optionally refresh.
 * 
 * @example
 * const { data, error } = await invokeWithExistingSession(
 *   "send-welcome-email",
 *   session,
 *   { body: { email: user.email } }
 * );
 */
export async function invokeWithExistingSession<T = any>(
  functionName: string,
  session: Session,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  try {
    let activeSession = session;
    
    // Check if token is expired and attempt refresh if needed
    if (isTokenExpired(session)) {
      if (opts.logErrors) {
        console.log(`[EdgeFunctionHelper] Session token expired for ${functionName}, attempting refresh...`);
      }
      
      const { session: refreshedSession, error: refreshError } = await getValidSession();
      
      if (refreshError || !refreshedSession) {
        const error = refreshError || new Error("Failed to refresh expired session");
        if (opts.logErrors) {
          console.error(`[EdgeFunctionHelper] Session refresh failed for ${functionName}:`, error);
        }
        return { data: null, error };
      }
      
      activeSession = refreshedSession;
    }
    
    // Invoke with retry logic
    const result = await retryWithBackoff(
      async () => {
        // eslint-disable-next-line no-restricted-syntax
        const { data, error } = await supabase.functions.invoke<T>(functionName, {
          body: opts.body,
          headers: {
            ...opts.headers,
            Authorization: `Bearer ${activeSession.access_token}`,
          },
        });
        
        if (error) throw error;
        return data;
      },
      {
        maxRetries: opts.maxRetries,
        initialDelayMs: opts.retryDelay,
      }
    );
    
    return { data: result, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    if (opts.logErrors) {
      console.error(`[EdgeFunctionHelper] Error invoking ${functionName}:`, error);
    }
    return { data: null, error };
  }
}

/**
 * Invoke a public edge function (no authentication required)
 * 
 * Includes retry logic for network failures and consistent error handling.
 * Use for endpoints that are publicly accessible (e.g., waitlist, webhooks).
 * 
 * @example
 * const { data, error } = await invokePublicEdgeFunction("send-verification-email", {
 *   body: { email: "user@example.com", confirmationUrl: "..." },
 *   maxRetries: 3
 * });
 */
export async function invokePublicEdgeFunction<T = any>(
  functionName: string,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const startTime = Date.now();
  
  try {
    // Invoke with retry logic
    const result = await retryWithBackoff(
      async () => {
        // eslint-disable-next-line no-restricted-syntax
        const { data, error } = await supabase.functions.invoke<T>(functionName, {
          body: opts.body,
          headers: opts.headers,
        });
        
        if (error) throw error;
        return data;
      },
      {
        maxRetries: opts.maxRetries,
        initialDelayMs: opts.retryDelay,
      }
    );
    
    trackEdgeFunctionCall(functionName, true, Date.now() - startTime);
    return { data: result, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    if (opts.logErrors) {
      console.error(`[EdgeFunctionHelper] Error invoking public function ${functionName}:`, error);
    }
    trackEdgeFunctionCall(functionName, false, Date.now() - startTime, error.message);
    return { data: null, error };
  }
}

/**
 * Fire-and-forget invocation for non-critical operations
 * 
 * Useful for analytics, logging, reputation tracking, etc. where you don't
 * need to wait for the result or handle errors in the UI.
 * 
 * Errors are logged but not thrown. Operation happens asynchronously.
 * 
 * @example
 * // Non-blocking analytics call
 * invokeNonBlocking("record-login-reputation", {
 *   headers: { Authorization: `Bearer ${token}` }
 * });
 * 
 * // Function returns immediately, doesn't block execution
 */
export function invokeNonBlocking(
  functionName: string,
  options: EdgeFunctionOptions = {}
): void {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  // Run async operation without awaiting
  setTimeout(async () => {
    try {
      // eslint-disable-next-line no-restricted-syntax
      await supabase.functions.invoke(functionName, {
        body: opts.body,
        headers: opts.headers,
      });
    } catch (err) {
      if (opts.logErrors) {
        console.error(`[EdgeFunctionHelper] Non-blocking invocation failed for ${functionName}:`, err);
      }
    }
  }, 0);
}

/**
 * Invoke an authenticated edge function and stream the response
 * 
 * This helper is for functions that return a ReadableStream (Server-Sent Events).
 * It handles authentication and returns the stream for consumption.
 * 
 * @example
 * const stream = await streamAuthenticatedEdgeFunction("generate-board", {
 *   body: { boardType: "SaaS" }
 * });
 * 
 * if (stream) {
 *   const reader = stream.getReader();
 *   const decoder = new TextDecoder();
 *   
 *   while (true) {
 *     const { done, value } = await reader.read();
 *     if (done) break;
 *     
 *     const chunk = decoder.decode(value);
 *     // Process SSE chunk
 *   }
 * }
 */
export async function streamAuthenticatedEdgeFunction(
  functionName: string,
  options: EdgeFunctionOptions = {}
): Promise<ReadableStream<Uint8Array> | null> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const startTime = Date.now();

  try {
    // Get valid session with token refresh if needed
    const { session, error: sessionError } = await getValidSession();

    if (sessionError || !session) {
      const error = sessionError || new Error("Authentication required");
      if (opts.logErrors) {
        console.error(`[EdgeFunctionHelper] Auth failed for ${functionName}:`, error);
      }
      trackEdgeFunctionCall(functionName, false, Date.now() - startTime, error.message);
      return null;
    }

    // Invoke the function
    // eslint-disable-next-line no-restricted-syntax
    const response = await supabase.functions.invoke(functionName, {
      body: opts.body,
      headers: {
        ...opts.headers,
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (response.error) {
      throw response.error;
    }

    if (!response.data) {
      throw new Error("No data returned from streaming function");
    }

    trackEdgeFunctionCall(functionName, true, Date.now() - startTime);
    return response.data as ReadableStream<Uint8Array>;

  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    if (opts.logErrors) {
      console.error(`[EdgeFunctionHelper] Error invoking streaming function ${functionName}:`, error);
    }
    trackEdgeFunctionCall(functionName, false, Date.now() - startTime, error.message);
    return null;
  }
}
